import type {
    Moment,
    MomentInstant,
    MomentOrdinal,
    MomentOrdinalSelector,
    MomentPart,
    MomentPartDeclaration,
    MomentPartSelector,
    MomentScalar,
    MomentSelector,
    MomentVocabulary,
} from '@/_contract/_~generated/directionMomentSchema';
import type { MomentCodec, MomentIssue } from '@/_contract/directionMoment';

const alternatives = <T>(value: T | readonly T[]): readonly T[] => Array.isArray(value) ? value : [value as T];

function matchesOrdinal(selector: MomentOrdinalSelector, ordinal: MomentOrdinal | undefined): boolean {
    if (!ordinal) return false;
    if (selector === 'first') return ordinal.index === 0;
    if (selector === 'last') return ordinal.count !== undefined && ordinal.index === ordinal.count - 1;
    return ordinal.index === selector;
}

export function matchesMomentPart(selector: MomentPartSelector, part: MomentPart): boolean {
    if (!alternatives(selector.kind).includes(part.kind)) return false;
    if (selector.ordinal !== undefined && !matchesOrdinal(selector.ordinal, part.ordinal)) return false;
    return Object.entries(selector.parameters ?? {}).every(([name, expected]) => {
        const actual = part.parameters?.[name];
        return actual !== undefined && alternatives<MomentScalar>(expected).includes(actual);
    });
}

/** Ordered-subsequence match: each frame selector consumes the next matching frame, outermost first. */
function matchesFrameSubsequence(selectors: readonly MomentPartSelector[], frames: readonly MomentPart[]): boolean {
    let next = 0;
    for (const selector of selectors) {
        while (next < frames.length && !matchesMomentPart(selector, frames[next])) next += 1;
        if (next === frames.length) return false;
        next += 1;
    }
    return true;
}

export function matchesMomentSelector(selector: MomentSelector, instant: MomentInstant): boolean {
    const { frames, step } = instant.moment;
    if (selector.step && !matchesMomentPart(selector.step, step)) return false;
    const within = selector.within ?? [];
    if (selector.framing === 'exact') {
        if (within.length !== frames.length || !within.every((part, index) => matchesMomentPart(part, frames[index]))) return false;
    } else if (!matchesFrameSubsequence(within, frames)) return false;
    if (selector.role !== undefined) return instant.role !== undefined && alternatives(selector.role).includes(instant.role);
    return true;
}

function checkPartSelector(
    selector: MomentPartSelector,
    declarations: Readonly<Record<string, MomentPartDeclaration>>,
    category: 'step' | 'frame',
    path: string,
): MomentIssue[] {
    const issues: MomentIssue[] = [];
    const kinds = alternatives(selector.kind);
    kinds.forEach((kind, index) => {
        if (!Object.hasOwn(declarations, kind)) {
            issues.push({ path: Array.isArray(selector.kind) ? `${path}/kind/${index}` : `${path}/kind`, message: `Unknown ${category} kind '${kind}'.` });
        }
    });
    const declared = kinds.filter(kind => Object.hasOwn(declarations, kind)).map(kind => [kind, declarations[kind]] as const);
    if (selector.ordinal !== undefined) {
        for (const [kind, declaration] of declared) {
            if (!declaration.ordinal) issues.push({ path: `${path}/ordinal`, message: `The ${category} kind '${kind}' has no ordinal.` });
        }
    }
    for (const [name, expected] of Object.entries(selector.parameters ?? {})) {
        const parameterPath = `${path}/parameters/${pointerToken(name)}`;
        for (const [kind, declaration] of declared) {
            const parameter = declaration.parameters?.[name];
            if (!parameter) {
                issues.push({ path: parameterPath, message: `The ${category} kind '${kind}' has no parameter '${name}'.` });
                continue;
            }
            for (const value of alternatives<MomentScalar>(expected)) {
                if (typeof value !== parameter.type) {
                    issues.push({ path: parameterPath, message: `Parameter '${name}' of '${kind}' expects a ${parameter.type}, not ${JSON.stringify(value)}.` });
                } else if (parameter.values && !parameter.values.includes(value)) {
                    issues.push({ path: parameterPath, message: `Parameter '${name}' of '${kind}' does not allow ${JSON.stringify(value)}.` });
                }
            }
        }
    }
    return issues;
}

const pointerToken = (name: string) => name.replaceAll('~', '~0').replaceAll('/', '~1');

function checkPart(
    part: MomentPart,
    declarations: Readonly<Record<string, MomentPartDeclaration>>,
    category: 'step' | 'frame',
    path: string,
): MomentIssue[] {
    if (!Object.hasOwn(declarations, part.kind)) return [{ path: `${path}/kind`, message: `Unknown ${category} kind '${part.kind}'.` }];
    const declaration = declarations[part.kind];
    const issues: MomentIssue[] = [];
    if (!declaration.ordinal && part.ordinal) issues.push({ path: `${path}/ordinal`, message: `The ${category} kind '${part.kind}' has no ordinal.` });
    if (part.ordinal && (!Number.isSafeInteger(part.ordinal.index) || part.ordinal.index < 0 || part.ordinal.index >= Number.MAX_SAFE_INTEGER
        || (part.ordinal.count !== undefined && (!Number.isSafeInteger(part.ordinal.count) || part.ordinal.count < 1)))) {
        issues.push({ path: `${path}/ordinal`, message: 'Ordinal index must be a nonnegative safe integer and count a positive safe integer.' });
    }
    if (part.ordinal?.count !== undefined && part.ordinal.index >= part.ordinal.count) {
        issues.push({ path: `${path}/ordinal`, message: `Ordinal index ${part.ordinal.index} is outside its count ${part.ordinal.count}.` });
    }
    for (const [name, value] of Object.entries(part.parameters ?? {})) {
        const parameter = declaration.parameters?.[name];
        const parameterPath = `${path}/parameters/${pointerToken(name)}`;
        if (!parameter) issues.push({ path: parameterPath, message: `The ${category} kind '${part.kind}' has no parameter '${name}'.` });
        else if (typeof value !== parameter.type) {
            issues.push({ path: parameterPath, message: `Parameter '${name}' of '${part.kind}' expects a ${parameter.type}, not ${JSON.stringify(value)}.` });
        } else if (typeof value === 'number' && !Number.isFinite(value)) {
            issues.push({ path: parameterPath, message: `Parameter '${name}' of '${part.kind}' must be finite.` });
        } else if (parameter.values && !parameter.values.includes(value)) {
            issues.push({ path: parameterPath, message: `Parameter '${name}' of '${part.kind}' does not allow ${JSON.stringify(value)}.` });
        }
    }
    return issues;
}

/** Checks a schema-valid moment against an adapter vocabulary; declared ordinals and parameters are optional, undeclared ones are issues. */
export function checkMoment(moment: Moment, vocabulary: MomentVocabulary): MomentIssue[] {
    return [
        ...moment.frames.flatMap((frame, index) => checkPart(frame, vocabulary.frames, 'frame', `/frames/${index}`)),
        ...checkPart(moment.step, vocabulary.steps, 'step', '/step'),
    ];
}

/** Canonical replay identity: outer frames/step, with one-based index then declared parameters. Counts are selection metadata. */
export function createMomentCodec(vocabulary: MomentVocabulary): MomentCodec {
    const encodePart = (part: MomentPart, declarations: Readonly<Record<string, MomentPartDeclaration>>): string => {
        const fields = part.ordinal ? [`index=${part.ordinal.index + 1}`] : [];
        for (const name of Object.keys(declarations[part.kind].parameters ?? {})) {
            if (name === 'index') throw new Error(`Moment parameter 'index' is reserved for the ordinal of '${part.kind}'.`);
            const value = part.parameters?.[name];
            if (value !== undefined) fields.push(`${encodeURIComponent(name)}=${encodeURIComponent(String(value))}`);
        }
        return `${encodeURIComponent(part.kind)}${fields.length ? `[${fields.join(',')}]` : ''}`;
    };
    return {
        encode(moment) {
            const issues = checkMoment(moment, vocabulary);
            if (issues.length > 0) {
                throw new Error(`Invalid moment ${JSON.stringify(moment)}: ${issues.map(issue => `${issue.path} ${issue.message}`).join('; ')}`);
            }
            return [
                ...moment.frames.map(frame => encodePart(frame, vocabulary.frames)),
                encodePart(moment.step, vocabulary.steps),
            ].join('/');
        },
    };
}

/** Unites vocabularies, such as a host's extension of an embedded adapter's vocabulary; a kind declared twice throws. */
export function composeMomentVocabularies(...vocabularies: readonly MomentVocabulary[]): MomentVocabulary {
    const compose = (category: 'steps' | 'frames') => {
        const declarations: Record<string, MomentPartDeclaration> = {};
        for (const vocabulary of vocabularies) {
            for (const [kind, declaration] of Object.entries(vocabulary[category])) {
                if (Object.hasOwn(declarations, kind)) throw new Error(`Moment vocabularies both declare the ${category === 'steps' ? 'step' : 'frame'} kind '${kind}'.`);
                declarations[kind] = declaration;
            }
        }
        return declarations;
    };
    return { steps: compose('steps'), frames: compose('frames') };
}

/** Checks a schema-valid selector against an adapter vocabulary; structural validation stays with the JSON Schema. */
export function checkMomentSelector(selector: MomentSelector, vocabulary: MomentVocabulary): MomentIssue[] {
    const issues: MomentIssue[] = [];
    if (selector.step) issues.push(...checkPartSelector(selector.step, vocabulary.steps, 'step', '/step'));
    (selector.within ?? []).forEach((part, index) => {
        issues.push(...checkPartSelector(part, vocabulary.frames, 'frame', `/within/${index}`));
    });
    if (selector.role !== undefined) {
        const steps = selector.step
            ? alternatives(selector.step.kind).filter(kind => Object.hasOwn(vocabulary.steps, kind))
            : Object.keys(vocabulary.steps);
        alternatives(selector.role).forEach((role, index) => {
            if (!steps.some(kind => vocabulary.steps[kind].roles?.includes(role))) {
                issues.push({
                    path: Array.isArray(selector.role) ? `/role/${index}` : '/role',
                    message: selector.step ? `No selected step kind declares the role '${role}'.` : `No step kind declares the role '${role}'.`,
                });
            }
        });
    }
    return issues;
}
